import React, { useEffect, useState } from 'react'

import { RxExternalLink } from '@react-icons/all-files/rx/RxExternalLink'
import { RxEnvelopeClosed } from '@react-icons/all-files/rx/RxEnvelopeClosed'

import { validateEmail } from '@disco/utils/validation'

import type { SourceCatalog } from 'types/track'

import { Flex, HoverCard, Text } from 'components/ds'

import styles from './catalog-info-popover.module.css'

const isHttpsUrl = (value: string | undefined): value is string => {
  if (!value) return false
  try {
    return new URL(value).protocol === 'https:'
  } catch {
    return false
  }
}

interface CatalogInfoPopoverProps {
  source: SourceCatalog
  children: React.ReactElement
}

const CatalogImageOrFallback = ({
  imageUrl,
  name,
}: {
  imageUrl?: string
  name: string
}) => {
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    setHasError(false)
  }, [imageUrl])

  if (imageUrl && !hasError) {
    return (
      <img
        src={imageUrl}
        alt={name}
        className={styles.catalogImage}
        onError={() => setHasError(true)}
      />
    )
  }

  return (
    <div aria-hidden="true" className={styles.catalogImageFallback}>
      <span>{name.charAt(0).toUpperCase()}</span>
    </div>
  )
}

export const CatalogInfoPopover = ({
  source,
  children,
}: CatalogInfoPopoverProps) => {
  const name =
    source.name?.trim() || source.business_name?.trim() || 'Untitled catalog'
  const trimmedOwnerEmail = source.owner_email?.trim()
  const ownerEmail =
    trimmedOwnerEmail && validateEmail(trimmedOwnerEmail)
      ? trimmedOwnerEmail
      : null
  return (
    <HoverCard.Root openDelay={300} closeDelay={100}>
      <HoverCard.Trigger data-testid="catalog-info-popover">
        {children}
      </HoverCard.Trigger>
      <HoverCard.Content side="bottom" align="start" className={styles.content}>
        <Flex direction="column" gap="2">
          <Flex align="center" gap="2">
            <CatalogImageOrFallback imageUrl={source.image_url} name={name} />
            <Text size="3" weight="bold">
              {name}
            </Text>
          </Flex>

          {source.description && (
            <Text size="2" className={styles.description}>
              {source.description}
            </Text>
          )}

          {source.track_count != null && (
            <Text size="2" className={styles.description}>
              {`${source.track_count.toLocaleString('en-US')} ${
                source.track_count === 1 ? 'track' : 'tracks'
              }`}
            </Text>
          )}

          {isHttpsUrl(source.url) && (
            <a
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.visitLink}
              onClick={e => e.stopPropagation()}
            >
              <RxExternalLink aria-hidden="true" />
              <Text size="2">Visit catalog</Text>
            </a>
          )}

          {ownerEmail && (
            <>
              <hr className={styles.divider} />

              <Text size="1" weight="medium" className={styles.ownerSection}>
                CATALOG OWNER
              </Text>

              <Flex align="center" gap="1">
                <RxEnvelopeClosed aria-hidden="true" />
                <Text size="2">{ownerEmail}</Text>
              </Flex>
            </>
          )}
        </Flex>
      </HoverCard.Content>
    </HoverCard.Root>
  )
}
